/**
 * Loading, empty and error states.
 *
 * The brief asks for all three explicitly, and they are grouped here because the
 * distinction between them is the point: a screen showing nothing must say
 * *which* nothing it means. "No products yet" invites an action; "Could not load
 * products" invites a retry; a skeleton says wait. Collapsing them into one blank
 * area is how a merchant concludes their catalogue has been deleted.
 */

import { AlertTriangle, RefreshCw, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

import { Button } from "./Button";

// ═════════════════════════════════════════════════════════════════════════════
// Loading
// ═════════════════════════════════════════════════════════════════════════════

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      // A skeleton is decoration standing in for content; announcing it would
      // read out meaningless placeholders. The container carries aria-busy.
      aria-hidden
      className={cn(
        "relative block overflow-hidden rounded-md bg-line/70",
        "after:absolute after:inset-0 after:animate-shimmer",
        "after:bg-gradient-to-r after:from-transparent after:via-white/60 after:to-transparent",
        className,
      )}
    />
  );
}

export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("space-y-3", className)} aria-busy>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Empty
// ═════════════════════════════════════════════════════════════════════════════

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  /** Plain language. The reader is a shopkeeper, not a developer. */
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-6 py-14 text-center",
        className,
      )}
    >
      {Icon && (
        <span className="mb-4 grid size-14 place-items-center rounded-full bg-canvas" aria-hidden>
          <Icon className="size-7 text-ink-400" strokeWidth={1.75} />
        </span>
      )}
      <h3 className="text-base font-semibold text-ink-900">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Error
// ═════════════════════════════════════════════════════════════════════════════

export interface ErrorStateProps {
  title?: string;
  description?: ReactNode;
  onRetry?: () => void;
  /** Shown small and selectable, so it can be read out to support. */
  requestId?: string;
  className?: string;
}

export function ErrorState({
  title = "Something went wrong",
  description = "We could not load this. Please try again.",
  onRetry,
  requestId,
  className,
}: ErrorStateProps) {
  return (
    <div
      // `alert` so assistive technology announces the failure rather than
      // leaving someone waiting for content that will never arrive.
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center px-6 py-14 text-center",
        className,
      )}
    >
      <span className="mb-4 grid size-14 place-items-center rounded-full bg-danger-50" aria-hidden>
        <AlertTriangle className="size-7 text-danger-500" strokeWidth={1.75} />
      </span>

      <h3 className="text-base font-semibold text-ink-900">{title}</h3>
      <p className="mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">{description}</p>

      {onRetry && (
        <Button
          className="mt-5"
          variant="secondary"
          onClick={onRetry}
          icon={<RefreshCw className="size-4" aria-hidden />}
        >
          Try again
        </Button>
      )}

      {requestId && (
        <p className="mt-4 select-all font-numeric text-xs text-ink-400">
          Reference: {requestId}
        </p>
      )}
    </div>
  );
}
